import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Plus } from "lucide-react";
import React from "react";

import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  PermissionGuard,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import { toast } from "@/components/ui/use-toast";
import { getPanelBrandSlug } from "@/config/config";
import { useI18n } from "@/context/i18n";
import * as insuranceCompaniesApi from "@/pages/insurance-companies/insurance-companies.api";

import { TableProducts } from "./components";
import { CreditProductForm } from "./components/products-form/credit-product-form";
import { InsuranceAutoForm } from "./components/products-form/insurance-auto-form";
import { InsuranceFuneralForm } from "./components/products-form/insurance-funeral-form";
import { InsuranceGroupLifeForm } from "./components/products-form/insurance-group-life-form";
import { InsurancePersonalAccidentForm } from "./components/products-form/insurance-personal-accident-form";
import { InsuranceWarrantyForm } from "./components/products-form/insurance-warranty-form";
import { isInfinityAssistanceProduct } from "./products.assistance-variant";
import { useProduct } from "./products.hooks";
import { EProductType } from "./products.type";
import {
  getProductCatalogEntriesForUi,
  type TProductCatalogEntry,
} from "./products-catalog";
import { useAllowedProductTypes } from "./use-allowed-product-types";

const FILTER_ALL = "__all__";
/** Filtro: assistência sem `product_integration_ref` (Soulhub). */
const FILTER_ASSISTANCE_SOULHUB = "__assistance_soulhub__";
/** Filtro: assistência com `product_integration_ref` (Infinity / sync). */
const FILTER_ASSISTANCE_INFINITY = "__assistance_infinity__";

function productTypeFilterLabel(type: string, t: (key: string) => string): string {
  const key = `product.type.${type}`;
  const translated = t(key);
  return translated === key ? type.replace(/_/g, " ") : translated;
}

export const ProductsPage: React.FC = () => {
  const { t, locale } = useI18n();
  const { allowedTypes } = useAllowedProductTypes();
  const panelBrand = getPanelBrandSlug();

  const catalogEntries = React.useMemo(() => {
    const entries = getProductCatalogEntriesForUi(allowedTypes, panelBrand);
    const displayLabel = (entry: TProductCatalogEntry) =>
      entry.id === EProductType.INSURANCE_ASSISTANCE && panelBrand === "soulhub"
        ? "Assistência - Soulhub"
        : t(entry.labelKey);
    return [...entries].sort((a, b) =>
      displayLabel(a).localeCompare(displayLabel(b), locale, {
        sensitivity: "base",
      }),
    );
  }, [allowedTypes, panelBrand, locale, t]);

  const {
    isProductFormOpen,
    setIsProductFormOpen,
    productForm,
    openProductForm,
    createProduct,
    updateProduct,
    deleteProduct,
    products,
    productType,
    setProductType,
    editingProductId,
    setEditingProductId,
    editingProduct,
    isEditing,
  } = useProduct();

  const [filterName, setFilterName] = React.useState("");
  const [filterSupplierId, setFilterSupplierId] =
    React.useState<string>(FILTER_ALL);
  const [filterType, setFilterType] = React.useState<string>(FILTER_ALL);

  const { data: insuranceCompanies } = useQuery({
    queryKey: ["insuranceCompanies"],
    queryFn: insuranceCompaniesApi.getAllInsuranceCompanies,
  });

  const supplierOptions = React.useMemo(() => {
    const list = insuranceCompanies ?? [];
    return [...list]
      .filter((c) => !!c.id)
      .sort((a, b) =>
        a.name.localeCompare(b.name, locale, { sensitivity: "base" }),
      );
  }, [insuranceCompanies, locale]);

  const typeFilterOptions = React.useMemo(() => {
    const rows: { value: string; label: string }[] = [];
    for (const type of allowedTypes) {
      if (type === EProductType.INSURANCE_ASSISTANCE) {
        rows.push(
          {
            value: FILTER_ASSISTANCE_INFINITY,
            label: t("products.filters.typeAssistanceInfinity"),
          },
          {
            value: FILTER_ASSISTANCE_SOULHUB,
            label: t("products.filters.typeAssistanceSoulhub"),
          },
        );
      } else {
        rows.push({
          value: type,
          label: productTypeFilterLabel(type, t),
        });
      }
    }
    return [...rows].sort((a, b) =>
      a.label.localeCompare(b.label, locale, { sensitivity: "base" }),
    );
  }, [allowedTypes, locale, t]);

  const filteredProducts = React.useMemo(() => {
    if (!products) {
      return undefined;
    }
    let list = [...products];
    const q = filterName.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => (p.name ?? "").toLowerCase().includes(q));
    }
    if (filterSupplierId !== FILTER_ALL) {
      list = list.filter((p) => p.insurance_company_id === filterSupplierId);
    }
    if (filterType !== FILTER_ALL) {
      if (filterType === FILTER_ASSISTANCE_SOULHUB) {
        list = list.filter(
          (p) =>
            p.type === EProductType.INSURANCE_ASSISTANCE &&
            !isInfinityAssistanceProduct(p),
        );
      } else if (filterType === FILTER_ASSISTANCE_INFINITY) {
        list = list.filter(
          (p) =>
            p.type === EProductType.INSURANCE_ASSISTANCE &&
            isInfinityAssistanceProduct(p),
        );
      } else {
        list = list.filter((p) => p.type === filterType);
      }
    }
    return list;
  }, [products, filterName, filterSupplierId, filterType]);

  const clearFilters = React.useCallback(() => {
    setFilterName("");
    setFilterSupplierId(FILTER_ALL);
    setFilterType(FILTER_ALL);
  }, []);

  return (
    <div className="flex min-h-0 w-full flex-col space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-3">
            {isProductFormOpen && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsProductFormOpen(false);
                  setProductType(null);
                  setEditingProductId(null);
                }}
                size="sm"
                className="p-3 hover:bg-muted/50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {productType
                ? productType === EProductType.INSURANCE_ASSISTANCE &&
                  panelBrand === "soulhub"
                  ? "Assistência - Soulhub"
                  : t(`product.type.${productType}`)
                : t("products.title")}
            </h1>
          </div>
          {!productType && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("products.description")}
            </p>
          )}
        </div>
        {!isProductFormOpen && (
          <div className="flex justify-end">
            <PermissionGuard permission="products:create">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    className="shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("products.button.new.product")}
                  </Button>
                </PopoverTrigger>
              <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0">
                <div className="grid max-h-[min(70vh,32rem)] gap-0 overflow-hidden">
                  <div className="space-y-2 border-b px-4 py-3">
                    <h4 className="leading-none font-medium">
                      {t("products.catalog.popover.title")}
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      {t("products.catalog.popover.description")}
                    </p>
                  </div>
                  <div className="overflow-y-auto px-2 py-2">
                    <Select
                      onValueChange={(value) => {
                        const entry = catalogEntries.find(
                          (e) => e.id === value
                        );
                        if (!entry) {
                          return;
                        }
                        if (!entry.implemented) {
                          toast({
                            title: t("products.catalog.coming_soon.title"),
                            description: `${t(entry.labelKey)} — ${t(
                              "products.catalog.coming_soon.description"
                            )}`,
                          });
                          return;
                        }
                        setEditingProductId(null);
                        setProductType(entry.productType!);
                        openProductForm();
                      }}
                    >
                      <SelectTrigger className="w-full min-w-0">
                        <SelectValue
                          placeholder={t(
                            "products.form.fields.product_type.placeholder"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent className="max-h-[min(60vh,24rem)]">
                        <SelectGroup>
                          {catalogEntries.map((entry) => (
                            <SelectItem key={entry.id} value={entry.id}>
                              {entry.id === EProductType.INSURANCE_ASSISTANCE &&
                              panelBrand === "soulhub"
                                ? "Assistência - Soulhub"
                                : t(entry.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            </PermissionGuard>
          </div>
        )}
      </div>
      <InsurancePersonalAccidentForm
        open={isProductFormOpen && productType === EProductType.INSURANCE_AP}
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      <InsuranceGroupLifeForm
        open={isProductFormOpen && productType === EProductType.INSURANCE_VG}
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      {panelBrand === "soulhub" &&
      !(isEditing && isInfinityAssistanceProduct(editingProduct)) ? (
        <InsuranceWarrantyForm
          open={
            isProductFormOpen &&
            productType === EProductType.INSURANCE_ASSISTANCE
          }
          onOpenChange={() => {}}
          form={productForm}
          isEditMode={isEditing}
          assistanceSoulhubMode
          productKind="warranty"
          onSubmit={(data) => {
            if (isEditing && editingProductId) {
              updateProduct(editingProductId, data);
            } else {
              createProduct(data);
            }
          }}
        />
      ) : (
        <InsuranceFuneralForm
          open={
            isProductFormOpen &&
            productType === EProductType.INSURANCE_ASSISTANCE
          }
          onOpenChange={() => {}}
          form={productForm}
          isEditMode={isEditing}
          onSubmit={(data) => {
            if (isEditing && editingProductId) {
              updateProduct(editingProductId, data);
            } else {
              createProduct(data);
            }
          }}
        />
      )}
      <InsuranceAutoForm
        key={`${editingProductId || "new"}-${productType || "vehicle"}`}
        open={
          isProductFormOpen &&
          (productType === EProductType.AUTO ||
            productType === EProductType.MOTO)
        }
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        productId={isEditing ? editingProductId : null}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      <InsuranceWarrantyForm
        open={
          isProductFormOpen &&
          (productType === EProductType.WARRANTY_INSURANCE ||
            productType === EProductType.THEFT_INSURANCE)
        }
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        productId={isEditing ? editingProductId : null}
        productKind={
          productType === EProductType.THEFT_INSURANCE ? "theft" : "warranty"
        }
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      <CreditProductForm
        open={isProductFormOpen && productType === EProductType.CREDIT}
        onOpenChange={() => {}}
        form={productForm}
        isEditMode={isEditing}
        productId={isEditing ? editingProductId : null}
        onSubmit={(data) => {
          if (isEditing && editingProductId) {
            updateProduct(editingProductId, data);
          } else {
            createProduct(data);
          }
        }}
      />
      {!isProductFormOpen && (
        <>
          <Card className="border-muted/60">
            <CardContent className="space-y-4 pt-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
                <div className="flex min-w-[min(100%,14rem)] flex-1 flex-col gap-2">
                  <Label htmlFor="products-filter-name">
                    {t("products.filters.name")}
                  </Label>
                  <Input
                    id="products-filter-name"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    placeholder={t("products.filters.namePlaceholder")}
                    className="w-full"
                  />
                </div>
                <div className="flex min-w-[min(100%,12rem)] flex-col gap-2 lg:max-w-xs lg:flex-1">
                  <Label htmlFor="products-filter-supplier">
                    {t("products.filters.supplier")}
                  </Label>
                  <Select
                    value={filterSupplierId}
                    onValueChange={setFilterSupplierId}
                  >
                    <SelectTrigger id="products-filter-supplier" className="w-full">
                      <SelectValue placeholder={t("products.filters.all")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[min(60vh,24rem)]">
                      <SelectGroup>
                        <SelectItem value={FILTER_ALL}>
                          {t("products.filters.all")}
                        </SelectItem>
                        {supplierOptions.map((c) => (
                          <SelectItem key={c.id} value={c.id!}>
                            {c.name} — {c.cnpj}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex min-w-[min(100%,12rem)] flex-col gap-2 lg:max-w-xs lg:flex-1">
                  <Label htmlFor="products-filter-type">
                    {t("products.filters.type")}
                  </Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger id="products-filter-type" className="w-full">
                      <SelectValue placeholder={t("products.filters.all")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[min(60vh,24rem)]">
                      <SelectGroup>
                        <SelectItem value={FILTER_ALL}>
                          {t("products.filters.all")}
                        </SelectItem>
                        {typeFilterOptions.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="shrink-0"
                  onClick={clearFilters}
                >
                  {t("products.filters.clear")}
                </Button>
              </div>
            </CardContent>
          </Card>
          <TableProducts
            products={filteredProducts}
            deleteProduct={deleteProduct}
          />
        </>
      )}
    </div>
  );
};
